import { useEffect, useState } from "react";
import {
  MoreVertical,
  Share2,
  PlusSquare,
  Chrome as ChromeIcon,
  Smartphone,
  Monitor,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

function detectPlatform() {
  if (typeof navigator === "undefined")
    return { browser: "unknown", os: "unknown", isStandalone: false };
  const ua = navigator.userAgent || "";
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || /Mobi|Tablet/.test(ua);
  let browser = "other";
  if (/Edg\//.test(ua)) browser = "edge";
  else if (/Chrome\//.test(ua) && !/OPR\//.test(ua)) browser = "chrome";
  else if (/Firefox\//.test(ua)) browser = "firefox";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "safari";
  let os = "other";
  if (isIOS) os = "ios";
  else if (isAndroid) os = "android";
  else if (/Win/.test(ua)) os = "windows";
  else if (/Mac/.test(ua)) os = "mac";
  else if (/Linux/.test(ua)) os = "linux";
  return { browser, os, isMobile, isIOS, isAndroid, isStandalone };
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3 items-start">
      <span className="shrink-0 w-6 h-6 border-2 border-[#0A0A0A] flex items-center justify-center text-[11px] font-extrabold font-mono">
        {n}
      </span>
      <span className="text-sm leading-relaxed">{children}</span>
    </li>
  );
}

export default function InstallGuide({ open, onOpenChange, installable }) {
  const [info, setInfo] = useState(detectPlatform());
  useEffect(() => {
    if (open) setInfo(detectPlatform());
  }, [open]);

  const renderInstructions = () => {
    if (info.isStandalone) {
      return (
        <div
          data-testid="install-guide-already"
          className="border border-[#16a34a] bg-[#F0FDF4] px-4 py-3 flex gap-2 items-center"
        >
          <CheckCircle2
            size={18}
            strokeWidth={2.5}
            className="text-[#16a34a] shrink-0"
          />
          <div className="text-sm">
            <span className="font-bold">Already installed.</span> You're using
            the standalone app right now. Look for the Ezbiz Invoice icon on
            your desktop / home screen.
          </div>
        </div>
      );
    }

    if (info.isIOS) {
      return (
        <div data-testid="install-guide-ios">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone size={16} strokeWidth={2.5} />
            <span className="text-xs font-bold uppercase tracking-widest">
              iPhone / iPad · Safari
            </span>
          </div>
          <ol className="space-y-2.5 list-none">
            <Step n={1}>
              Tap the{" "}
              <Share2 size={14} className="inline align-middle" />{" "}
              <span className="font-bold">Share</span> button in the bottom
              toolbar.
            </Step>
            <Step n={2}>
              Scroll down and tap{" "}
              <PlusSquare size={14} className="inline align-middle" />{" "}
              <span className="font-bold">Add to Home Screen</span>.
            </Step>
            <Step n={3}>
              Tap <span className="font-bold">Add</span> in the top right. The
              app icon appears on your home screen.
            </Step>
          </ol>
        </div>
      );
    }

    if (info.isAndroid) {
      return (
        <div data-testid="install-guide-android">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone size={16} strokeWidth={2.5} />
            <span className="text-xs font-bold uppercase tracking-widest">
              Android · Chrome
            </span>
          </div>
          <ol className="space-y-2.5 list-none">
            <Step n={1}>
              Tap the <MoreVertical size={14} className="inline align-middle" />{" "}
              menu (top right).
            </Step>
            <Step n={2}>
              Tap <span className="font-bold">Install app</span> (or{" "}
              <span className="font-bold">Add to Home screen</span>).
            </Step>
            <Step n={3}>
              Confirm by tapping <span className="font-bold">Install</span>.
              The app icon lands on your home screen and launches without
              browser bars.
            </Step>
          </ol>
        </div>
      );
    }

    if (info.browser === "firefox") {
      return (
        <div data-testid="install-guide-firefox">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} strokeWidth={2.5} />
            <span className="text-xs font-bold uppercase tracking-widest">
              Firefox doesn't support PWA install
            </span>
          </div>
          <p className="text-sm leading-relaxed mb-3">
            Firefox dropped PWA install support on desktop. To install Ezbiz
            Invoice as a desktop app, use{" "}
            <span className="font-bold">Chrome</span>,{" "}
            <span className="font-bold">Edge</span>, or{" "}
            <span className="font-bold">Brave</span> instead.
          </p>
          <p className="text-xs text-[#52525B] leading-relaxed">
            Firefox can still bookmark and run the site offline; you just
            won't get a desktop icon.
          </p>
        </div>
      );
    }

    if (info.browser === "safari") {
      return (
        <div data-testid="install-guide-safari-mac">
          <div className="flex items-center gap-2 mb-3">
            <Monitor size={16} strokeWidth={2.5} />
            <span className="text-xs font-bold uppercase tracking-widest">
              Safari · macOS
            </span>
          </div>
          <ol className="space-y-2.5 list-none">
            <Step n={1}>
              In the menu bar, click{" "}
              <span className="font-bold">File → Add to Dock</span>.
            </Step>
            <Step n={2}>
              Confirm the app name and click{" "}
              <span className="font-bold">Add</span>.
            </Step>
            <Step n={3}>
              The app appears in your Dock and Launchpad. Note: Safari's PWA
              support is limited; for the best experience use Chrome / Edge
              on macOS.
            </Step>
          </ol>
        </div>
      );
    }

    // Chrome / Edge / desktop default
    return (
      <div data-testid="install-guide-chrome">
        <div className="flex items-center gap-2 mb-3">
          <ChromeIcon size={16} strokeWidth={2.5} />
          <span className="text-xs font-bold uppercase tracking-widest">
            Chrome / Edge · Desktop
          </span>
        </div>
        <p className="text-xs text-[#52525B] mb-3">
          The browser hasn't shown the auto-install prompt yet. You can still
          install manually:
        </p>
        <ol className="space-y-2.5 list-none">
          <Step n={1}>
            Look for the install icon{" "}
            <span className="font-mono px-1.5 py-0.5 border border-[#0A0A0A] text-[11px]">
              ⊕
            </span>{" "}
            on the right side of the address bar. Click it.
          </Step>
          <Step n={2}>
            Or open the{" "}
            <MoreVertical size={14} className="inline align-middle" /> menu →{" "}
            <span className="font-bold">Cast, save and share</span> →{" "}
            <span className="font-bold">Install Ezbiz Invoice…</span>{" "}
            <span className="text-[#A1A1AA]">
              (Edge: <span className="font-bold">Apps → Install this site as an app</span>)
            </span>
            .
          </Step>
          <Step n={3}>
            Click <span className="font-bold">Install</span>. The app launches
            in its own window and gets a desktop / Start menu icon.
          </Step>
        </ol>
        <div className="mt-4 text-[11px] text-[#52525B] border-t border-[#E5E5E5] pt-3">
          <span className="font-bold">Why no auto-prompt?</span> Chrome only
          shows the in-app install button after some site engagement (e.g. a
          few seconds of interaction). Try saving an invoice or opening Setup,
          then click <span className="font-bold">Install</span> in the header
          again — the prompt should appear.
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="install-guide"
        className="sm:max-w-[560px] rounded-none border-2 border-[#0A0A0A] p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-5 border-b-2 border-[#0A0A0A] bg-[#FAFAFA]">
          <DialogTitle className="text-xl font-extrabold tracking-tighter">
            Install Ezbiz Invoice
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            {installable
              ? "Click Install in the header to add the app to your desktop."
              : "Your browser hasn't offered the one-click install yet — here's how to add it manually:"}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[70vh] overflow-y-auto scroll-clean">
          {renderInstructions()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
